import { ImageIcon } from 'lucide-react';
import type { AdCardData } from '../../pages/canvas/canvas-types';

interface AdCardProps {
  card: AdCardData;
  onSelect: (card: AdCardData) => void;
}

function MetricPill({ label, value }: { label: string; value: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '2px 7px',
        borderRadius: 20,
        background: '#F1F5F9',
        border: '1px solid #DCE3ED',
        fontSize: 10,
        fontWeight: 600,
        color: '#526074',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: '#94A3B8', fontWeight: 500 }}>{label}</span>
      {value}
    </span>
  );
}

function fmtPct(v?: number) {
  if (v === undefined || v === null) return null;
  return `${v.toFixed(2)}%`;
}
function fmtRoas(v?: number) {
  if (v === undefined || v === null) return null;
  return `${v.toFixed(2)}x`;
}
function fmtCpc(v?: number) {
  if (v === undefined || v === null) return null;
  return `$${v.toFixed(2)}`;
}

export function AdCard({ card, onSelect }: AdCardProps) {
  const pills: Array<{ label: string; value: string }> = [];
  const ctr = fmtPct(card.metrics.ctr);
  const roas = fmtRoas(card.metrics.roas);
  const cpc = fmtCpc(card.metrics.cpc);
  if (ctr) pills.push({ label: 'CTR ', value: ctr });
  if (roas) pills.push({ label: 'ROAS ', value: roas });
  if (cpc) pills.push({ label: 'CPC ', value: cpc });

  return (
    <button
      data-testid={`ad-card-${card.id}`}
      onClick={() => onSelect(card)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
        border: '1px solid #DCE3ED',
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'pointer',
        textAlign: 'left',
        padding: 0,
        minWidth: 160,
        maxWidth: 200,
        flexShrink: 0,
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.10)';
        (e.currentTarget as HTMLButtonElement).style.borderColor = '#C8D3E2';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLButtonElement).style.borderColor = '#DCE3ED';
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: '100%',
          aspectRatio: '4/3',
          background: '#F7F9FC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {card.thumbnail_url ? (
          <img
            src={card.thumbnail_url}
            alt={card.name}
            data-testid={`ad-card-thumbnail-${card.id}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => {
              const parent = (e.target as HTMLImageElement).parentElement;
              if (parent) {
                (e.target as HTMLImageElement).style.display = 'none';
                parent.innerHTML = `<div style="display:flex;flex-direction:column;align-items:center;gap:4px;color:#94A3B8"><svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='3' width='18' height='18' rx='2'/><circle cx='8.5' cy='8.5' r='1.5'/><path d='m21 15-5-5L5 21'/></svg></div>`;
              }
            }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, color: '#94A3B8' }}>
            <ImageIcon style={{ width: 22, height: 22 }} />
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '10px 10px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span
          data-testid={`ad-card-name-${card.id}`}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#1A1A1A',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {card.name}
        </span>
        {pills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {pills.map((p) => (
              <MetricPill key={p.label} label={p.label} value={p.value} />
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

interface AdCardsRowProps {
  cards: AdCardData[];
  onSelect: (card: AdCardData) => void;
}

export function AdCardsRow({ cards, onSelect }: AdCardsRowProps) {
  if (cards.length === 0) return null;

  const useVertical = cards.length > 3;

  return (
    <div
      data-testid="ad-cards-row"
      style={{
        marginTop: 12,
        display: useVertical ? 'flex' : 'flex',
        flexDirection: useVertical ? 'column' : 'row',
        gap: 10,
        overflowX: useVertical ? 'visible' : 'auto',
        paddingBottom: useVertical ? 0 : 4,
      }}
    >
      {useVertical
        ? cards.map((card) => (
            <AdCard key={card.id} card={card} onSelect={onSelect} />
          ))
        : cards.map((card) => (
            <AdCard key={card.id} card={card} onSelect={onSelect} />
          ))}
    </div>
  );
}
